import { createTheme } from '@mui/material/styles';

// Variables CSS converties en JavaScript (basées sur settingStyle.css)
const cssVariables = {
  // Colors
  colors: {
    b5c0cc: '#B5C0CC',
    f5d4d8: '#F5D4D8',
    b32535: '#B32535',
    f7f8fa: '#F7F8FA',
    '2e6cb4': '#2E6CB4',
    aadcf2: '#AADCF2',
    ed694e: '#ED694E',
    '1d1d1b': '#1D1D1B',
    f1853e: '#F1853E',
    d6e2f0: '#D6E2F0',
    '707070': '#707070',
    ced7e0: '#CED7E0',
    ff6a49: '#FF6A49',
    '003056': '#003056',
    e9f0f7: '#E9F0F7',
    '1e6eef': '#1E6EEF',
    '1473f5': '#1473F5',
    '00002f': '#00002F',
    '0056d7': '#0056D7',
    '162e58': '#162E58',
    '0046e3': '#0046E3',
    '0c3058': '#0C3058',
    '7eafff': '#7EAFFF',
    ff00ff: '#FF00FF',
    ff00ff17: '#FF00FF17',
    '0000ff': '#0000FF',
    '0000ff14': '#0000FF14',
    ffffff: '#FFFFFF',
    e8edf6: '#E8EDF6',
    '687e97': '#687E97',
  },
  
  // Font weights
  fontWeights: {
    light: 300,
    normal: 400,
    medium: 500,
    semiBold: 600,
    bold: 700,
  },
  
  // Font sizes
  fontSizes: {
    xs: '6px',
    sm: '12px',
    base: '14px',
    md: '16px',
    lg: '18px',
    xl: '22px',
    '2xl': '24px',
    '3xl': '28px',
    '4xl': '32px',
    '5xl': '40px',
    '6xl': '56px',
  },
  
  // Line heights
  lineHeights: {
    tight: '12px',
    snug: '14px',
    normal: '16px',
    relaxed: '17px',
    loose: '19px',
    '22': '22px',
    '24': '24px',
    '27': '27px',
    '28': '28px',
    '30': '30px',
    '33': '33px',
    '47': '47px',
    '66': '66px',
  }
};

const theme = createTheme({
  palette: {
    primary: {
      main: cssVariables.colors['0c3058'], // Bleu foncé principal
      light: cssVariables.colors['162e58'],
      dark: cssVariables.colors['003056'],
      contrastText: cssVariables.colors.ffffff,
    },
    secondary: {
      main: cssVariables.colors.ff6a49, // Orange principal
      light: cssVariables.colors.f1853e,
      dark: cssVariables.colors.ed694e,
      contrastText: cssVariables.colors.ffffff,
    },
    background: {
      default: cssVariables.colors.e8edf6, // Fond principal
      paper: cssVariables.colors.ffffff,
      light: cssVariables.colors.f7f8fa,
      lighter: cssVariables.colors.e9f0f7,
    },
    text: {
      primary: cssVariables.colors['0c3058'], // Texte principal bleu foncé
      secondary: cssVariables.colors['162e58'], // Texte secondaire
      disabled: cssVariables.colors['687e97'], // Texte désactivé
    },
    grey: {
      50: cssVariables.colors.e9f0f7,
      100: cssVariables.colors.d6e2f0,
      200: cssVariables.colors.ced7e0,
      300: cssVariables.colors.b5c0cc,
      400: cssVariables.colors['687e97'],
      500: cssVariables.colors['707070'],
    },
    info: {
      main: cssVariables.colors['1e6eef'],
      light: cssVariables.colors['7eafff'],
      dark: cssVariables.colors['0056d7'],
    },
    success: {
      main: cssVariables.colors['2e6cb4'],
      light: cssVariables.colors.aadcf2,
    },
    error: {
      main: cssVariables.colors.b32535,
      light: cssVariables.colors.f5d4d8,
    },
  },
  typography: {
    fontFamily: '"Rubik", "Roboto", "Helvetica", "Arial", sans-serif',
    
    // H1 - Grand titre (56pt)
    h1: {
      fontFamily: 'Rubik',
      fontStyle: 'normal',
      fontWeight: cssVariables.fontWeights.bold,
      fontSize: cssVariables.fontSizes['6xl'], // 56px
      lineHeight: cssVariables.lineHeights['66'], // 66px
      letterSpacing: '0px',
      color: cssVariables.colors.ff6a49, // Orange
    },
    
    // H2 - Titre moyen (40pt)
    h2: {
      fontFamily: 'Rubik',
      fontStyle: 'normal',
      fontWeight: cssVariables.fontWeights.bold,
      fontSize: cssVariables.fontSizes['5xl'], // 40px
      lineHeight: cssVariables.lineHeights['47'], // 47px
      letterSpacing: '0px',
      color: cssVariables.colors.ff6a49, // Orange
    },
    
    // H3 - Titre (28pt)
    h3: {
      fontFamily: 'Rubik',
      fontStyle: 'normal',
      fontWeight: cssVariables.fontWeights.semiBold,
      fontSize: cssVariables.fontSizes['4xl'], // 32px
      lineHeight: cssVariables.lineHeights['28'], // 28px
      letterSpacing: '0px',
      color: cssVariables.colors['0c3058'], // Bleu foncé
    },
    
    // H4 - Sous-titre (28pt)
    h4: {
      fontFamily: 'Rubik',
      fontStyle: 'normal',
      fontWeight: cssVariables.fontWeights.bold,
      fontSize: cssVariables.fontSizes['3xl'], // 28px
      lineHeight: cssVariables.lineHeights['33'], // 33px
      letterSpacing: '0px',
      color: cssVariables.colors.ff6a49, // Orange
    },
    
    // H5 - Titre section (24pt)
    h5: {
      fontFamily: 'Rubik',
      fontStyle: 'normal',
      fontWeight: cssVariables.fontWeights.medium,
      fontSize: cssVariables.fontSizes['2xl'], // 24px
      lineHeight: cssVariables.lineHeights['28'], // 28px
      letterSpacing: '0px',
      color: cssVariables.colors['162e58'], // Bleu moyen
    },
    
    // H6 - Petit titre (22pt)
    h6: {
      fontFamily: 'Rubik',
      fontStyle: 'normal',
      fontWeight: cssVariables.fontWeights.medium,
      fontSize: cssVariables.fontSizes.xl, // 22px
      lineHeight: cssVariables.lineHeights['27'], // 27px
      letterSpacing: '0px',
      color: cssVariables.colors['0c3058'], // Bleu foncé
    },
    
    // Body1 - Texte principal (18pt)
    body1: {
      fontFamily: 'Rubik',
      fontStyle: 'normal',
      fontWeight: cssVariables.fontWeights.normal,
      fontSize: cssVariables.fontSizes.lg, // 18px
      lineHeight: cssVariables.lineHeights['28'], // 28px
      letterSpacing: '0px',
      color: cssVariables.colors['0c3058'], // Bleu foncé
    },
    
    // Body2 - Texte secondaire (16pt)
    body2: {
      fontFamily: 'Rubik',
      fontStyle: 'normal',
      fontWeight: cssVariables.fontWeights.normal,
      fontSize: cssVariables.fontSizes.md, // 16px
      lineHeight: cssVariables.lineHeights['24'], // 24px
      letterSpacing: '0px',
      color: cssVariables.colors['003056'], // Bleu très foncé
    },
    
    // Caption - Petits textes (14pt)
    caption: {
      fontFamily: 'Rubik',
      fontStyle: 'normal',
      fontWeight: cssVariables.fontWeights.normal,
      fontSize: cssVariables.fontSizes.base, // 14px
      lineHeight: cssVariables.lineHeights.relaxed, // 17px
      letterSpacing: '0px',
      color: cssVariables.colors['162e58'], // Bleu moyen
    },
    
    // Button text
    button: {
      fontFamily: 'Rubik',
      fontStyle: 'normal',
      fontWeight: cssVariables.fontWeights.medium,
      fontSize: cssVariables.fontSizes.base, // 14px
      lineHeight: cssVariables.lineHeights.relaxed, // 17px
      letterSpacing: '0px',
      textTransform: 'none',
    },
    
    // Variants personnalisés pour éviter la répétition des styles
    heroText: {
      fontFamily: 'Rubik',
      fontStyle: 'normal',
      fontWeight: cssVariables.fontWeights.medium, // 500
      fontSize: '20px',
      lineHeight: 1.6,
      color: cssVariables.colors.ffffff,
      textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
      // wordBreak: 'break-word',
      // hyphens: 'auto',
      '@media (max-width: 768px)': {
        fontSize: '16px',
        lineHeight: 1.5,
      },
      '@media (max-width: 480px)': {
        fontSize: '15px',
        lineHeight: 1.4,
      }
    },
    
    heroSubtext: {
      fontFamily: 'Rubik',
      fontStyle: 'normal',
      fontWeight: cssVariables.fontWeights.light, // 300
      fontSize: '18px',
      lineHeight: 1.6,
      color: cssVariables.colors.ffffff,
      textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
    },
    
    whiteTitle: {
      fontFamily: 'Rubik',
      fontStyle: 'normal',
      fontWeight: cssVariables.fontWeights.semiBold, // 600
      fontSize: '24px',
      lineHeight: 1.4,
      color: cssVariables.colors.ffffff,
      textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
    },
    
    overlayText: {
      fontFamily: 'Rubik',
      fontStyle: 'normal',
      fontWeight: cssVariables.fontWeights.normal, // 400
      fontSize: '16px',
      lineHeight: 1.5,
      color: cssVariables.colors.ffffff,
      textShadow: '1px 1px 2px rgba(0,0,0,0.4)',
    },
    
    // Variants pour les titres du hero avec responsive
    heroMainTitle: {
      fontFamily: 'Rubik',
      fontStyle: 'normal',
      fontWeight: cssVariables.fontWeights.bold, // 700
      fontSize: '2.2rem', // Mobile first - plus petit pour les textes longs
      lineHeight: 1.2,
      color: '#FF6B35',
      textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
      wordBreak: 'break-word',
      hyphens: 'auto',
      '@media (min-width: 768px)': {
        fontSize: '2.8rem',
        lineHeight: 1.1,
      },
      '@media (min-width: 960px)': {
        fontSize: '3.2rem',
      },
    },
    
    heroSubtitle: {
      fontFamily: 'Rubik',
      fontStyle: 'normal',
      fontWeight: cssVariables.fontWeights.bold, // 700
      fontSize: '1.2rem', // Mobile first - plus petit
      lineHeight: 1.3,
      color: '#FF6B35',
      textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
      wordBreak: 'break-word',
      hyphens: 'auto',
      '@media (min-width: 768px)': {
        fontSize: '1.5rem',
        lineHeight: 1.2,
      },
      '@media (min-width: 960px)': {
        fontSize: '1.7rem',
      },
    },
    
    stateLabel: {
      fontFamily: 'Rubik',
      fontStyle: 'normal',
      fontWeight: cssVariables.fontWeights.semiBold, // 600
      fontSize: '1rem',
      color: cssVariables.colors.ffffff,
      textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
    },
  },
  
  components: {
    // Global styles
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          fontFamily: 'Rubik, sans-serif',
        },
        body: {
          backgroundColor: cssVariables.colors.e8edf6,
          color: cssVariables.colors['0c3058'],
        },
      },
    },
    
    // Button styles
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontFamily: 'Rubik',
          fontWeight: cssVariables.fontWeights.medium,
          padding: '12px 24px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
          '&:focus-visible': {
            outline: `3px solid ${cssVariables.colors['003056']}`,   
            outlineOffset: '2px',
          },
        },
        contained: {
          backgroundColor: cssVariables.colors.ff6a49,
          color: cssVariables.colors.ffffff,
          '&:hover': {
            backgroundColor: cssVariables.colors.ed694e,
            transform: 'translateY(-1px)',
          },
        },
        outlined: {
          borderColor: cssVariables.colors.ff6a49,
          color: cssVariables.colors.ff6a49,
          '&:hover': {
            borderColor: cssVariables.colors.ed694e,
            backgroundColor: `${cssVariables.colors.ff6a49}10`,
          },
        },
        // Variant pour bouton hero
        hero: {
          backgroundColor: '#FF6B35',
          color: cssVariables.colors['0c3058'],
          padding: '12px 32px',
          fontSize: '1rem',
          fontWeight: cssVariables.fontWeights.semiBold,
          borderRadius: 8,
          textTransform: 'none',
        //   boxShadow: '0 4px 12px rgba(255, 107, 53, 0.4)',
          '&:hover': {
            backgroundColor: '#FF6B35',
            transform: 'translateY(-1px)',
            boxShadow: '0 6px 16px rgba(255, 107, 53, 0.5)',
          },
          transition: 'all 0.3s ease',
        },
        
        // Bouton bleu foncé avec texte blanc
        primary: {
          backgroundColor: cssVariables.colors['0c3058'],
          color: cssVariables.colors.ffffff,
          padding: '12px 24px',
          fontSize: '1rem',
          fontWeight: cssVariables.fontWeights.medium,
          borderRadius: 8,
          textTransform: 'none',
          boxShadow: `0 2px 8px ${cssVariables.colors['0c3058']}40`,
          '&:hover': {
            backgroundColor: cssVariables.colors['003056'],
            transform: 'translateY(-1px)',
            boxShadow: `0 4px 12px ${cssVariables.colors['0c3058']}60`,
          },
          transition: 'all 0.3s ease',
        },
        
        // Bouton blanc avec texte bleu foncé
        secondary: {
          backgroundColor: cssVariables.colors.ffffff,
          color: cssVariables.colors['0c3058'],
          padding: '12px 24px',
          fontSize: '1rem',
          fontWeight: cssVariables.fontWeights.medium,
          borderRadius: 8,
          textTransform: 'none',
          border: `2px solid ${cssVariables.colors['0c3058']}`,
          boxShadow: `0 2px 8px ${cssVariables.colors['0c3058']}20`,
          '&:hover': {
            backgroundColor: cssVariables.colors.e8edf6,
            transform: 'translateY(-1px)',
            boxShadow: `0 4px 12px ${cssVariables.colors['0c3058']}30`,
            borderColor: cssVariables.colors['162e58'],
          },
          transition: 'all 0.3s ease',
        },
        
        // Bouton compact bleu foncé
        primaryCompact: {
          backgroundColor: cssVariables.colors['0c3058'],
          color: cssVariables.colors.ffffff,
          padding: '8px 16px',
          fontSize: '0.875rem',
          fontWeight: cssVariables.fontWeights.medium,
          borderRadius: 6,
          textTransform: 'none',
          minHeight: 'auto',
          '&:hover': {
            backgroundColor: cssVariables.colors['003056'],
            transform: 'translateY(-1px)',
          },
      //     '&:focus-visible': {
      //       outline: `3px solid ${cssVariables.colors['ffffff']}`,   
      //       outlineOffset: '2px',
      // },          
          transition: 'all 0.3s ease',
        },
        
        // Bouton compact blanc
        secondaryCompact: {
          backgroundColor: cssVariables.colors.ffffff,
          color: cssVariables.colors['0c3058'],
          padding: '8px 16px',
          fontSize: '0.875rem',
          fontWeight: cssVariables.fontWeights.medium,
          borderRadius: 6,
          textTransform: 'none',
          border: `1px solid ${cssVariables.colors['0c3058']}`,
          minHeight: 'auto',
          '&:hover': {
            backgroundColor: cssVariables.colors.e8edf6,
            transform: 'translateY(-1px)',
          },
          transition: 'all 0.3s ease',
        },
        
        // Bouton large bleu foncé
        primaryLarge: {
          backgroundColor: cssVariables.colors['0c3058'],
          color: cssVariables.colors.ffffff,
          padding: '16px 32px',
          fontSize: '1.125rem',
          fontWeight: cssVariables.fontWeights.semiBold,
          borderRadius: 8,
          textTransform: 'none',
          boxShadow: `0 4px 12px ${cssVariables.colors['0c3058']}40`,
          '&:hover': {
            backgroundColor: cssVariables.colors['003056'],
            transform: 'translateY(-2px)',
            boxShadow: `0 6px 16px ${cssVariables.colors['0c3058']}60`,
          },
          transition: 'all 0.3s ease',
        },
        
        // Bouton large blanc
        secondaryLarge: {
          backgroundColor: cssVariables.colors.ffffff,
          color: cssVariables.colors['0c3058'],
          padding: '16px 32px',
          fontSize: '1.125rem',
          fontWeight: cssVariables.fontWeights.semiBold,
          borderRadius: 8,
          textTransform: 'none',
          border: `2px solid ${cssVariables.colors['0c3058']}`,
          boxShadow: `0 4px 12px ${cssVariables.colors['0c3058']}20`,
          '&:hover': {
            backgroundColor: cssVariables.colors.e8edf6,
            transform: 'translateY(-2px)',
            boxShadow: `0 6px 16px ${cssVariables.colors['0c3058']}30`,
            borderColor: cssVariables.colors['162e58'],
          },
          transition: 'all 0.3s ease',
        },
      },
    },
    
    // Card styles
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: `0 2px 8px ${cssVariables.colors['0c3058']}20`,
          border: `1px solid ${cssVariables.colors.d6e2f0}`,
          backgroundColor: cssVariables.colors.ffffff,
          '&:hover': {
            boxShadow: `0 8px 24px ${cssVariables.colors['0c3058']}25`,
            transform: 'translateY(-2px)',
          },
          transition: 'all 0.3s ease',
        },
      },
    },
    
    // AppBar styles
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: cssVariables.colors.ffffff,
          color: cssVariables.colors['0c3058'],
          boxShadow: `0 1px 3px ${cssVariables.colors['0c3058']}20`,
        },
      },
    },
    
    // Typography overrides pour les classes spécifiques
    MuiTypography: {
      styleOverrides: {
        // Style pour les titres blancs
        root: {
          '&.white-text': {
            color: cssVariables.colors.ffffff,
          },
          '&.light-text': {
            fontWeight: cssVariables.fontWeights.light,
            color: cssVariables.colors['687e97'],
          },
          '&.bold-text': {
            fontWeight: cssVariables.fontWeights.bold,
          },
          // Styles pour hero slider
          '&.hero-text': {
            fontWeight: cssVariables.fontWeights.medium,
            fontSize: '20px',
            lineHeight: 1.5,
            color: cssVariables.colors.ffffff,
            textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
          },
          '&.hero-subtext': {
            fontWeight: cssVariables.fontWeights.light,
            fontSize: '18px',
            lineHeight: 1.6,
            color: cssVariables.colors.ffffff,
            textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
          },
          '&.white-title': {
            fontWeight: cssVariables.fontWeights.semiBold,
            fontSize: '24px',
            lineHeight: 1.4,
            color: cssVariables.colors.ffffff,
            textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
          },
          '&.overlay-text': {
            fontWeight: cssVariables.fontWeights.normal,
            fontSize: '16px',
            lineHeight: 1.5,
            color: cssVariables.colors.ffffff,
            textShadow: '1px 1px 2px rgba(0,0,0,0.4)',
          },
          '&.hero-main-title': {
            fontWeight: cssVariables.fontWeights.bold,
            fontSize: '2.5rem',
            lineHeight: 1.1,
            color: '#FF6B35',
            textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
            '@media (min-width: 960px)': {
              fontSize: '3.5rem',
            },
          },
          '&.hero-subtitle': {
            fontWeight: cssVariables.fontWeights.semiBold,
            fontSize: '1.3rem',
            lineHeight: 1.2,
            color: '#FF6B35',
            textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
            '@media (min-width: 960px)': {
              fontSize: '1.8rem',
            },
          },
          '&.state-label': {
            fontWeight: cssVariables.fontWeights.semiBold,
            fontSize: '1rem',
            color: cssVariables.colors.ffffff,
            textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
          },
        },
      },
    },
    
    // Links
    MuiLink: {
      styleOverrides: {
        root: {
          color: cssVariables.colors['0046e3'],
          textDecoration: 'none',
          fontWeight: cssVariables.fontWeights.light,
          '&:hover': {
            color: cssVariables.colors['0056d7'],
            textDecoration: 'underline',
          },
        },
      },
    },
    
    // Accordion styles
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: `1px solid ${cssVariables.colors.d6e2f0}`,
          boxShadow: 'none',
          backgroundColor: cssVariables.colors.ffffff,
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: '8px 0',
          },
        },
      },
    },
    
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          backgroundColor: cssVariables.colors.e9f0f7,
          borderRadius: '8px 8px 0 0',
          '&.Mui-expanded': {
            borderRadius: '8px 8px 0 0',
          },
        },
      },
    },
  },
  
  shape: {
    borderRadius: 8,
  },
  
  // Breakpoints
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
});

export default theme; 